/**
 * Due-date helpers for todos/reminders.
 *
 * The API serialises a todo's due date as a civil date (`YYYY-MM-DD`) — there is
 * no time component, so it must be interpreted in the user's local calendar
 * rather than parsed as a UTC instant (which `new Date('2026-09-05')` does and
 * which shifts it to e.g. 1 AM in BST). Full ISO instants are still supported
 * for time-specific due dates.
 */

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export const isDateOnly = (dueDate: string): boolean => DATE_ONLY.test(dueDate);

/** Parse a due date into a local `Date`; date-only values become local midnight. */
export function parseDueDate(dueDate: string): Date {
  if (isDateOnly(dueDate)) {
    const [year, month, day] = dueDate.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(dueDate);
}

/**
 * Whether a due date has passed. A date-only due date is only overdue once the
 * whole day is over in local time; an instant is overdue as soon as it has passed.
 */
export function isDueDateOverdue(
  dueDate: string,
  now: Date = new Date(),
): boolean {
  const due = parseDueDate(dueDate);
  if (isDateOnly(dueDate)) {
    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );
    return due < startOfToday;
  }
  return due < now;
}

/**
 * Format a due date for display, e.g. "Fri, 5 Sep" or "Fri, 5 Sep at 3:30 PM".
 * Date-only values never show a time. `locale` defaults to en-US (the
 * authenticated app is English-only); localized public pages pass their own.
 */
export function formatDueDate(
  dueDate: string,
  options: { month?: 'short' | 'long'; locale?: string } = {},
): string {
  const d = parseDueDate(dueDate);
  const locale = options.locale ?? 'en-US';
  const datePart = d.toLocaleDateString(locale, {
    weekday: 'short',
    day: 'numeric',
    month: options.month ?? 'short',
  });
  if (isDateOnly(dueDate) || (d.getHours() === 0 && d.getMinutes() === 0)) {
    return datePart;
  }
  const timePart = d
    .toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' })
    .replace(':00', '');
  return locale.startsWith('en')
    ? `${datePart} at ${timePart}`
    : `${datePart}, ${timePart}`;
}
